import Link from "next/link";
import {
  SideNavigationV2Props,
  type ThreadsSidebarComponent,
} from "skedyul-ui/src/components/ui/SideNavigationV2";
import { SideNavigationComponentProps } from "../sideNavigation.schema";

import { close as closeSidebar } from "@store/features/layout/sideNavigationSlice";
import {
  setThreadSidebarFilter,
  setThreadSidebarSearch,
  setThreadSidebarContactSort,
  updateThread,
  toggleThreadPin,
  fetchThreadsList,
  fetchMoreThreads,
} from "@store/features/threads/threadSlice";
import { store } from "@store/index";
import type { AppDispatch } from "@store/index";

import type { ThreadListRow } from "@store/features/threads/threadMappers";
import type {
  ThreadItem,
  ThreadParticipant,
  ThreadFilterType,
  CommunicationChannel,
} from "skedyul-ui/src/components/ui/ThreadsSidebar";
import { resolveContactTileDisplayName } from "./resolveContactTileDisplayName";

function mapThreadToItem(thread: ThreadListRow): ThreadItem {
  return {
    id: thread.id,
    title: thread.title,
    lastMessageAt: thread.lastMessageAt,
    lastMessagePreview: thread.lastMessagePreview,
    lastActivityAt: thread.lastActivityAt ?? null,
    unreadCount: thread.unreadCount,
    participants: thread.participants.map(
      (p): ThreadParticipant => ({
        id: p.id,
        displayName:
          p.kind === "CONTACT"
            ? resolveContactTileDisplayName(p.displayName, p.associations)
            : p.displayName,
        avatarUrl: p.avatarUrl,
        kind: p.kind,
        contactId: p.contactId ?? null,
        memberId: p.memberId ?? null,
        contactActivity: p.contactActivity ?? null,
        imageSeed: p.associations?.[0]?.imageSeed ?? null,
      }),
    ),
    routingParticipantId: thread.routingParticipantId ?? null,
    communicationChannelId: thread.communicationChannelId ?? null,
    hasAttention: (thread.pendingApprovalCount ?? 0) > 0,
    routingPattern: thread.routingPattern ?? null,
    routingPatternDisplay: thread.routingPatternDisplay ?? null,
    routingAddress: thread.routingAddress ?? null,
    isExclusiveFeed: thread.isExclusiveFeed ?? false,
    // Status indicator fields
    hasActiveWorkflow: thread.hasActiveWorkflow ?? false,
    hasActiveAgent: thread.hasActiveAgent ?? false,
    pendingApprovalCount: thread.pendingApprovalCount ?? 0,
    hasScheduledMessage: thread.hasScheduledMessage ?? false,
    nextScheduledAt: thread.nextScheduledAt ?? null,
    pinnedAt: thread.pinnedAt ?? null,
    kind: thread.kind,
  };
}

export const sideThreadListSchema = (
  args: SideNavigationComponentProps<
    ThreadsSidebarComponent,
    { subdomain: string }
  >,
): SideNavigationV2Props<ThreadsSidebarComponent> => {
  const subdomain = args.routerContext.params?.subdomain;
  const pathname = args.routerContext.pathname;
  const state = args.state;

  // Get thread data from Redux
  const threadItems = state.threads?.items ?? [];
  const exclusiveFeedItems = state.threads?.exclusiveFeedItems ?? [];

  // Stable list order state
  const listLoadedAt = state.threads?.listLoadedAt ?? null;
  const pendingNewThreadCount = state.threads?.pendingNewThreads?.length ?? 0;
  const listHasMore = state.threads?.listHasMore ?? false;
  const listLoadingMore = state.threads?.listLoadingMore ?? false;
  const conversationContactCount =
    state.threads?.conversationContactCount ?? null;

  // Get current member ID
  const currentMemberId = state.workplace.currentMember.data?.id ?? null;

  // Get selected thread from pathname
  const threadPathMatch = pathname.match(/\/threads\/([^/?]+)/);
  const selectedThreadId = threadPathMatch?.[1] ?? null;

  // Sidebar tab is persisted via useSyncThreadSidebarFilter (sessionStorage).
  const activeFilter: ThreadFilterType =
    state.threads?.sidebarFilter ?? "inbox";
  const searchQuery = state.threads?.sidebarSearch ?? "";
  const contactSort = state.threads?.sidebarContactSort ?? "last_activity";

  // Get communication channels from CRM state
  const rawChannels = state.crm?.communicationChannels?.data ?? [];
  const channels: CommunicationChannel[] = rawChannels.map((ch) => ({
    id: ch.id,
    name: ch.name,
    type: ch.handle,
    icon: ch.icon,
    appIconUrl: ch.appIconUrl,
  }));

  // Map threads to sidebar item format, filtering out TESTING threads
  const threads: ThreadItem[] = threadItems
    .filter((t) => t.kind !== "TESTING")
    .map(mapThreadToItem);
  const exclusiveFeedThreads: ThreadItem[] =
    searchQuery.trim().length > 0
      ? []
      : exclusiveFeedItems
          .filter((t) => t.kind !== "TESTING")
          .map(mapThreadToItem);

  return {
    side: args.position,
    isOpen: state.sideNavigation[args.position].isOpen,
    width: "300px",
    component: {
      component: "ThreadsSidebar",
      props: {
        threads,
        exclusiveFeedThreads,
        selectedThreadId,
        currentMemberId,
        activeFilter,
        searchQuery,
        channels,
        onFilterChange: (filter: ThreadFilterType) => {
          store.dispatch(setThreadSidebarFilter(filter));
        },
        onSearchChange: (query: string) => {
          store.dispatch(setThreadSidebarSearch(query));
        },
        contactSort,
        onContactSortChange: (sort) => {
          store.dispatch(setThreadSidebarContactSort(sort));
        },
        onThreadSelect: () => {
          if (args.viewPortIsMobile) {
            store.dispatch(closeSidebar({ position: args.position }));
          }
        },
        onNewThread: () => {
          if (args.viewPortIsMobile) {
            store.dispatch(closeSidebar({ position: args.position }));
          }
        },
        getThreadHref: (threadId: string) => {
          const base = `/${subdomain}/threads/${threadId}`;
          const trimmedSearch = searchQuery.trim();
          if (!trimmedSearch) return base;
          return `${base}?q=${encodeURIComponent(trimmedSearch)}`;
        },
        getNewThreadHref: () => `/${subdomain}/threads/new`,
        LinkComponent: Link,
        onThreadRename: (threadId: string, newTitle: string) => {
          void (store.dispatch as AppDispatch)(
            updateThread({ threadId, title: newTitle }),
          );
        },
        onToggleThreadPin: (threadId: string, pinned: boolean) => {
          void (store.dispatch as AppDispatch)(
            toggleThreadPin({ threadId, pinned }),
          );
        },
        // Stable list order props
        listLoadedAt,
        pendingNewThreadCount,
        hasMore: listHasMore,
        loadingMore: listLoadingMore,
        totalConversationContactCount: conversationContactCount,
        onAcceptNewThreads: () => {
          void (store.dispatch as AppDispatch)(fetchThreadsList());
        },
        onLoadMore: () => {
          void (store.dispatch as AppDispatch)(fetchMoreThreads());
        },
      },
    },
  };
};
